// jwtAxios.js
import axios from 'axios';
import { getCookie, removeCookie, setCookie } from './cookieUtil'; // 쿠키 유틸리티 (경로에 맞게 수정하세요)

const API_BASE_URL = import.meta.env.VITE_API_URL;

const jwtAxios = axios.create({
    baseURL: API_BASE_URL,
});

/**
 * 요청 인터셉터
 *
 * 각 요청이 시작될 때 쿠키에 저장된 accessToken을 읽어와
 * Authorization 헤더에 "Bearer {accessToken}" 형태로 첨부합니다.
 *
 * @param {object} config - Axios 요청 설정 객체
 * @returns {object} 수정된 config 객체
 */
jwtAxios.interceptors.request.use(
    (config) => {
        // 쿠키에서 accessToken 읽어오기
        const accessToken = getCookie('accessToken');
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/**
 * 실패한 토큰 갱신 대기 요청들을 처리하는 함수
 *
 * 토큰 갱신 요청이 완료되면, 실패 대기열에 등록된 모든 요청에 대해
 * 새 토큰 또는 에러를 전달하여 대기 중인 Promise들을 해결합니다.
 *
 * @param {Error|null} error - 토큰 갱신 실패 시 에러 객체
 * @param {string|null} token - 새로 발급된 access token
 */
let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * 응답 인터셉터
 *
 * 응답 결과를 그대로 반환하거나, 에러 발생 시 처리합니다.
 * 특히 401 Unauthorized 에러가 발생하고 아직 재시도되지 않은 요청에 대해
 * refreshToken을 사용해 accessToken 재발급을 시도하고, 새 토큰을 적용한 후 원래 요청을 재시도합니다.
 *
 * @param {object} response - Axios 응답 객체
 * @returns {object} 정상 응답 또는 재요청 결과
 */
jwtAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        const originalRequest = error.config;

        // 401 에러 및 아직 재시도하지 않은 요청을 처리합니다.
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            // 만약 현재 토큰 갱신 중이라면, 대기열에 이 요청을 추가합니다.
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest.headers.Authorization = 'Bearer ' + token;
                    return jwtAxios(originalRequest);
                });
            }

            // 재시도 플래그를 설정하고, 토큰 갱신을 시작합니다.
            originalRequest._retry = true;
            isRefreshing = true;
            const refreshToken = getCookie('refreshToken'); // 쿠키에서 refreshToken 획득

            return new Promise((resolve, reject) => {
                console.log('Attempting to refresh token...');
                console.log('Current refresh token:', refreshToken);
                
                axios
                    .post(`${API_BASE_URL}auth/refresh`, { refreshToken }, {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(({ data }) => {
                        console.log('Token refresh successful:', data);
                        const newAccessToken = data.accessToken;

                        // 새로 받은 userInfo도 필요한 경우 저장
                        if (data.userInfo) {
                            // userInfo 저장 로직
                        }
                        
                        setCookie('accessToken', newAccessToken, 1);
                        isRefreshing = false; // 리프레시 상태 초기화
                        jwtAxios.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
                        processQueue(null, newAccessToken);
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                        resolve(jwtAxios(originalRequest));
                    })
                    .catch((err) => {
                        console.error('Token refresh failed:', err.response?.data || err.message);
                        // 에러 메시지 확인
                        const errorMessage = err.response?.data?.message || 'Token refresh failed';
                        if (errorMessage === 'Refresh token has expired') {
                            // 리프레시 토큰이 만료된 경우 로그아웃 처리
                            // 쿠키 삭제 및 로그인 페이지로 리다이렉트
                            removeCookie('accessToken');
                            removeCookie('refreshToken');
                            window.location.href = 'admin/login';
                        }
                        processQueue(err, null);
                        reject(err);
                    })
            });
        }
        // 그 외의 에러는 그대로 reject 처리
        return Promise.reject(error);
    }
);

export default jwtAxios;